/**
 * Job Details to be passed when navigating to Interview Prep from job card.
 */
export interface JSTInterviewPrepJobDetails {
    /**
     * The job key.
     */
    jobKey?: string | null;

    /**
     * The job title.
     */
    jobTitle?: string | null;
}

/**
 * Generic Url-target type.
 */
type Url = {
    type: 'Url';

    /**
     * A valid url.
     */
    url: string;
};

/**
 * Serp targets.
 */
export type JSTSerpTarget = Url;

/**
 * Factory methods for creating JSTSerpTarget instances.
 */

export const JSTSerpTarget = {
    /**
     * Creates JSTSerpTarget described by a URL
     */
    url(url: string): Url {
        return {
            type: 'Url',
            url,
        };
    },
};

/**
 * JobKey ViewJob target type
 */
type JobKey = {
    type: 'JobKey';
    /**
     * The job key
     */
    jobKey: string;
};

/**
 * ViewJob Target.
 */
export type JSTViewJobTarget = Url | JobKey;

/**
 * Factory methods for creating JSTViewJobTarget instances.
 */

export const JSTViewJobTarget = {
    /**
     * Creates JSTViewJobTarget described by a URL
     */
    url(url: string): Url {
        return {
            type: 'Url',
            url,
        };
    },

    /**
     * Creates JSTViewJobTarget described by a JobKey
     */
    jobKey(jobKey: string): JobKey {
        return {
            type: 'JobKey',
            jobKey,
        };
    },
};

/**
 * Home landing screen type
 */
type Home = {
    type: 'Home';
};

/**
 * Serp landing screen type
 */
type Serp = {
    type: 'Serp';
    /**
     * The Serp target for the user's search.
     */
    target: JSTSerpTarget;
};

/**
 * Pathfinder landing screen type
 */
type Pathfinder = {
    type: 'Pathfinder';

    /**
     * Optional specific Pathfinder url path. If null, user will be taken to the Pathfinder home page.
     */
    path?: string | null;
};

/**
 * MyJobs landing screen type
 */
type MyJobs = {
    type: 'MyJobs';
};

/**
 * Ace / Career Scout / Coworker root screen.
 */
type Ace = {
    type: 'Ace';
};

/**
 * Messaging landing screen type
 */
type Messaging = {
    type: 'Messaging';
};

/**
 * Profile landing screen type
 */
type Profile = {
    type: 'Profile';
};

/**
 * AceNewConversation landing screen type
 */
type AceNewConversation = {
    type: 'AceNewConversation';
    /**
     * Conversation starter message from the user
     */
    content?: string | null;
    /**
     * Agent type to nudge the conversation down a particular path
     */
    agentType?: string | null;
};

/**
 * AceConversation landing screen type
 */
type AceConversation = {
    type: 'AceConversation';
    /**
     * Id of an existing conversation
     */
    conversationId: string;
    /**
     * Conversation starter message from the user
     */
    content?: string | null;
    /**
     * Agent type to nudge the conversation down a particular path
     */
    agentType?: string | null;
};

/**
 * ViewJob landing screen type
 */
type ViewJob = {
    type: 'ViewJob';
    /**
     * See JSTViewJobTarget
     */
    target: JSTViewJobTarget;
};

type ViewJobFeed = {
    type: 'ViewJobFeed';
    /**
     * The homepageTk to view.
     */
    homepageTk: string;
    /**
     * The job key to view.
     */
    jobKey: string;
    /**
     * The viewJob url to view.
     */
    url: string;
};

/**
 * UpdateEmail landing screen type
 */
type UpdateEmail = {
    type: 'UpdateEmail';
};

/**
 * Profile exclusions landing screen type
 */
type ProfileExclusions = {
    type: 'ProfileExclusions';
};

/**
 * EbaAdLink landing screen type
 */
type EbaAdLink = {
    type: 'EbaAdLink';
    /**
     * The specific EBA URL to navigate to. Any specific URL must be one of the set of
     * EBA URLs, otherwise an IllegalArgumentException will be thrown at runtime.
     * @throws IllegalArgumentException url is not a valid/known Eba url.
     */
    url: string;
};

/**
 * SupportUrl landing screen type
 */
type SupportUrl = {
    type: 'SupportUrl';
    /**
     * The optional specific support URL to navigate to (null for the default support
     * page). Any specific URL must be one of the set of approved support URLs, otherwise an
     * IllegalArgumentException will be thrown at runtime.
     * @throws IllegalArgumentException url is not a valid/known support url.
     */
    url?: string | null;
};

/**
 * CompanyPage landing screen type
 */
type CompanyPage = {
    type: 'CompanyPage';

    /**
     * The URL of the page that initiated the navigation to this company page.
     */
    sourceUrl: string;

    /**
     * The URL of the company page to navigate to.
     */
    targetUrl: string;

    /**
     * Optional URL to use when sharing the company page. If null, no share button will be displayed
     * in the landing screen's Top Navigation Bar.
     */
    shareUrl?: string | null;

    /**
     * Optional message to include when sharing the company page.
     */
    shareMessage?: string | null;

    /**
     * Optional tracking key for share analytics.
     */
    shareTk?: string | null;
};

/**
 * TermsOfService landing screen type
 */
type TermsOfService = {
    type: 'TermsOfService';
};

/**
 * PrivacyPolicy landing screen type
 */
type PrivacyPolicy = {
    type: 'PrivacyPolicy';
};

/**
 * CCPA landing screen type
 */
type CCPA = {
    type: 'CCPA';
};

/**
 * CookiePolicy landing screen type
 */
type CookiePolicy = {
    type: 'CookiePolicy';
};

/**
 * IndeedNumberOneJobSearchSiteClaims landing screen type
 */
type IndeedNumberOneJobSearchSiteClaims = {
    type: 'IndeedNumberOneJobSearchSiteClaims';
};

/**
 * InterviewPrep landing screen type
 */
type InterviewPrep = {
    type: 'InterviewPrep';
    /**
     * The SUID associated to the occupation.
     */
    occupationSuid?: string | null;
    /**
     * The label associated with the occupation.
     */
    occupationLabel?: string | null;
    /**
     * The job details.
     */
    jobDetails?: JSTInterviewPrepJobDetails | null;
};

/**
 * MessagingConversation landing screen type
 */
type MessagingConversation = {
    type: 'MessagingConversation';
    /**
     * The ID of the conversation.
     */
    conversationId: string;
    /**
     * Optional key-value pairs to pass to the conversation. Null if not present.
     */
    optionalArgs?: Record<string, string | number | boolean> | null;
};

/**
 * ScheduleApplyApplicationReview landing screen type
 */
type ScheduleApplyApplicationReview = {
    type: 'ScheduleApplyApplicationReview';
    /**
     * The application ID for the scheduled apply application.
     */
    applicationId: string;
};

/**
 * MyJobs Tab Names
 */
export const MyJobsSubTab = {
    SAVED: 'saved',
    INVITATIONS: 'invitations',
    APPLIED: 'applied',
    INTERVIEWS: 'interviews',
    ARCHIVED: 'archived',
    GHOSTWRITERAUTOAPPLY: 'ghostwriterAutoApply',
} as const;

/**
 * Type representing valid MyJobs sub tab names
 */
export type MyJobsSubTabName = (typeof MyJobsSubTab)[keyof typeof MyJobsSubTab];

/**
 * MyJobsSubTab landing screen type
 */
type MyJobsSubTabLandingScreen = {
    type: 'MyJobsSubTab';
    /**
     * The name of the sub tab.
     */
    subTab: MyJobsSubTabName;
};

/**
 * Profile form mode type
 */
export type ProfileFormMode = 'add' | 'edit' | 'list' | 'suggestion';

/**
 * ProfileForms landing screen type - for presenting standalone profile forms
 * without the ProfileHeader background. Used for debug menu testing.
 */
type ProfileForms = {
    type: 'ProfileForms';
    /**
     * The section type to show (e.g., 'language', 'workExperience', 'education')
     */
    sectionType: string;
    /**
     * The form mode: 'add' for new entries, 'edit' for existing, 'list' for viewing list, 'suggestion' for suggestions
     */
    mode: string;
    /**
     * Optional item ID for edit mode (identifies which item to edit)
     */
    itemId?: string | null;
    /**
     * Optional prefill data for the form
     */
    prefillData?: Record<string, unknown> | null;
};

/**
 * Registration promo screen type
 */
type RegPromo = {
    type: 'RegPromo';
};

/**
 * ResumeGeneration landing screen type
 */
type ResumeGeneration = {
    type: 'ResumeGeneration';
};

/**
 * Defines the set of well-defined screens available to be navigated to within the native app.
 */
export type JSTLandingScreen =
    | Home
    | Serp
    | MyJobs
    | Ace
    | Messaging
    | Profile
    | Pathfinder
    | AceNewConversation
    | AceConversation
    | ViewJob
    | ViewJobFeed
    | UpdateEmail
    | ProfileExclusions
    | EbaAdLink
    | TermsOfService
    | PrivacyPolicy
    | CCPA
    | CookiePolicy
    | IndeedNumberOneJobSearchSiteClaims
    | SupportUrl
    | CompanyPage
    | InterviewPrep
    | MessagingConversation
    | ScheduleApplyApplicationReview
    | MyJobsSubTabLandingScreen
    | ProfileForms
    | RegPromo
    | ResumeGeneration;

/**
 * Factory methods for creating JSTLandingScreen instances
 */

export const JSTLandingScreen = {
    /**
     * Creates a Home landing screen
     */
    home(): Home {
        return {
            type: 'Home',
        };
    },

    /**
     * Creates a Serp landing screen
     */
    serp(target: JSTSerpTarget): Serp {
        return {
            type: 'Serp',
            target,
        };
    },

    /**
     * Creates a MyJobs landing screen
     */
    myJobs(): MyJobs {
        return {
            type: 'MyJobs',
        };
    },

    /**
     * Creates an Ace landing screen
     */
    ace(): Ace {
        return {
            type: 'Ace',
        };
    },

    /**
     * Creates a Messaging landing screen
     */
    messaging(): Messaging {
        return {
            type: 'Messaging',
        };
    },

    /**
     * Creates a Profile landing screen
     */
    profile(): Profile {
        return {
            type: 'Profile',
        };
    },

    /**
     * Creates a Pathfinder landing screen
     */
    pathfinder(path?: string | null): Pathfinder {
        return {
            type: 'Pathfinder',
            path: path ?? null,
        };
    },

    /**
     * Creates an AceNewConversation landing screen
     */
    aceNewConversation(content?: string | null, agentType?: string | null): AceNewConversation {
        return {
            type: 'AceNewConversation',
            content,
            agentType,
        };
    },

    /**
     * Creates an AceConversation landing screen
     */
    aceConversation(
        conversationId: string,
        content?: string | null,
        agentType?: string | null,
    ): AceConversation {
        return {
            type: 'AceConversation',
            conversationId,
            content,
            agentType,
        };
    },

    /**
     * Creates a ViewJob landing screen
     */
    viewJob(target: JSTViewJobTarget): ViewJob {
        return {
            type: 'ViewJob',
            target,
        };
    },

    /**
     * Creates a ViewJobFeed landing screen
     */
    viewJobFeed(homepageTk: string, jobKey: string, url: string): ViewJobFeed {
        return {
            type: 'ViewJobFeed',
            homepageTk,
            jobKey,
            url,
        };
    },

    /**
     * Creates an UpdateEmail landing screen
     */
    updateEmail(): UpdateEmail {
        return {
            type: 'UpdateEmail',
        };
    },

    /**
     * Creates a ProfileExclusions landing screen
     */
    profileExclusions(): ProfileExclusions {
        return {
            type: 'ProfileExclusions',
        };
    },

    /**
     * Creates an EbaAdLink landing screen
     */
    ebaAdLink(url: string): EbaAdLink {
        return {
            type: 'EbaAdLink',
            url,
        };
    },

    /**
     * Creates a SupportUrl landing screen
     */
    supportUrl(url?: string | null): SupportUrl {
        return {
            type: 'SupportUrl',
            url,
        };
    },

    /**
     * Creates a CompanyPage landing screen
     */
    companyPage(
        sourceUrl: string,
        targetUrl: string,
        shareUrl?: string | null,
        shareMessage?: string | null,
        shareTk?: string | null,
    ): CompanyPage {
        return {
            type: 'CompanyPage',
            sourceUrl,
            targetUrl,
            shareUrl,
            shareMessage,
            shareTk,
        };
    },

    /**
     * Creates a TermsOfService landing screen
     */
    termsOfService(): TermsOfService {
        return {
            type: 'TermsOfService',
        };
    },

    /**
     * Creates a PrivacyPolicy landing screen
     */
    privacyPolicy(): PrivacyPolicy {
        return {
            type: 'PrivacyPolicy',
        };
    },

    /**
     * Creates a CCPA landing screen
     */
    ccpa(): CCPA {
        return {
            type: 'CCPA',
        };
    },

    /**
     * Creates a CookiePolicy landing screen
     */
    cookiePolicy(): CookiePolicy {
        return {
            type: 'CookiePolicy',
        };
    },

    /**
     * Creates an IndeedNumberOneJobSearchSiteClaims landing screen
     */
    indeedNumberOneJobSearchSiteClaims(): IndeedNumberOneJobSearchSiteClaims {
        return {
            type: 'IndeedNumberOneJobSearchSiteClaims',
        };
    },

    /**
     * Creates an InterviewPrep landing screen
     */
    interviewPrep(
        occupationSuid?: string | null,
        occupationLabel?: string | null,
        jobDetails?: JSTInterviewPrepJobDetails | null,
    ): InterviewPrep {
        return {
            type: 'InterviewPrep',
            occupationSuid,
            occupationLabel,
            jobDetails,
        };
    },

    /**
     * Creates a MessagingConversation landing screen
     */
    messagingConversation(
        conversationId: string,
        optionalArgs?: Record<string, string | number | boolean> | null,
    ): MessagingConversation {
        return {
            type: 'MessagingConversation',
            conversationId,
            optionalArgs: optionalArgs ?? null,
        };
    },

    /**
     * Creates a ScheduleApplyApplicationReview landing screen
     */
    scheduleApplyApplicationReview(applicationId: string): ScheduleApplyApplicationReview {
        return {
            type: 'ScheduleApplyApplicationReview',
            applicationId,
        };
    },

    /**
     * Creates a myJobsSubTab landing screen
     */
    myJobsSubTab(subTab: MyJobsSubTabName): MyJobsSubTabLandingScreen {
        return {
            type: 'MyJobsSubTab',
            subTab,
        };
    },

    /**
     * Creates a ProfileForms landing screen for presenting standalone profile forms.
     * Use this for debug menu testing - shows ONLY the form, no ProfileHeader background.
     *
     * @param sectionType - The form section (e.g., 'language')
     * @param mode - The form mode ('add', 'edit', or 'suggestion')
     * @param itemId - Optional item ID for edit mode
     * @param prefillData - Optional data to prefill the form
     */
    profileForms(
        sectionType: string,
        mode: string,
        itemId?: string | null,
        prefillData?: Record<string, unknown> | null,
    ): ProfileForms {
        return {
            type: 'ProfileForms',
            sectionType,
            mode,
            itemId: itemId ?? null,
            prefillData: prefillData ?? null,
        };
    },

    /**
     * Creates a RegPromo landing screen
     */
    regPromo(): RegPromo {
        return {
            type: 'RegPromo',
        };
    },

    /**
     * Creates a ResumeGeneration landing screen
     */
    resumeGeneration(): ResumeGeneration {
        return {
            type: 'ResumeGeneration',
        };
    },
};
